import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGlobalContext } from 'GlobalContext';
import {
  createStoreGroupCampaign,
  deleteStoreGroup,
  getStoreGroupDetail,
  renameStoreGroup,
  updateStoreGroupCampaign,
} from 'services/storeGroup';
import type {
  CreateGroupCampaignPayload,
  GroupCampaignResponse,
  StoreGroup,
} from 'services/interfaces/storeGroup';
import { usePermissions } from 'shared/hooks/usePermissions';
import CreateGroupCampaignModal from './components/CreateGroupCampaignModal';
import ManageInstallationsSection from './components/ManageInstallationsSection';
import GroupClientsTab from './components/report/GroupClientsTab';
import GroupReportTab from './components/report/GroupReportTab';

function toReais(cents: number): number {
  return Math.round(cents) / 100;
}

function toCents(reais: number): number {
  return Math.round(reais * 100);
}

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

// A resposta do backoffice pode devolver `channels` como string[] (mesmo shape
// que já é usado pra criar a campanha) ou como objetos { type }, dependendo do
// que o fiber devolver por baixo — normaliza pros dois casos.
function normalizeChannels(
  channels: GroupCampaignResponse['channels'] | undefined,
): string[] {
  if (!channels) return [];
  return channels.map((c) =>
    typeof c === 'string' ? c : (c as { type: string }).type,
  );
}

const CampaignEditForm: React.FC<{
  campaign: GroupCampaignResponse;
  canUpdate: boolean;
  onSaved: () => void;
}> = ({ campaign, canUpdate, onSaved }) => {
  const [name, setName] = useState(campaign.name);
  const [cashbackPercentage, setCashbackPercentage] = useState(
    campaign.cashback_percentage,
  );
  const [minimumOrderValue, setMinimumOrderValue] = useState(
    toReais(campaign.minimum_order_value),
  );
  const [minimumRedeemOrderValue, setMinimumRedeemOrderValue] = useState(
    toReais(campaign.minimum_redeem_order_value),
  );
  const [pinExpirationDays, setPinExpirationDays] = useState(
    Math.round((campaign.pin_expiration_time ?? 0) / 24),
  );
  const [endAt, setEndAt] = useState(toDateInput(campaign.end_at));
  const [channels, setChannels] = useState<string[]>(
    normalizeChannels(campaign.channels),
  );
  const [saving, setSaving] = useState(false);

  const canSubmit = name.trim().length > 0 && channels.length > 0;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await updateStoreGroupCampaign(campaign.group_id, {
        name: name.trim(),
        cashback_percentage: cashbackPercentage,
        minimum_order_value: toCents(minimumOrderValue),
        minimum_redeem_order_value: toCents(minimumRedeemOrderValue),
        pin_expiration_time: pinExpirationDays
          ? pinExpirationDays * 24
          : undefined,
        end_at: endAt ? new Date(endAt).toISOString() : undefined,
        channels,
      });
      toast.success('Campanha atualizada.');
      onSaved();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Falha ao atualizar a campanha.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={4}>
      <HStack>
        <Badge colorScheme={campaign.status === 'ACTIVE' ? 'green' : 'gray'}>
          {campaign.status}
        </Badge>
      </HStack>

      <FormControl isRequired>
        <FormLabel>Nome da campanha</FormLabel>
        <Input
          value={name}
          isDisabled={saving || !canUpdate}
          onChange={(e) => setName(e.target.value)}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Percentual de cashback (%)</FormLabel>
        <NumberInput
          value={cashbackPercentage}
          min={0}
          max={100}
          isDisabled={saving || !canUpdate}
          onChange={(_, v) => setCashbackPercentage(Number.isNaN(v) ? 0 : v)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </FormControl>

      <HStack align="start">
        <FormControl>
          <FormLabel>Pedido mínimo p/ acumular (R$)</FormLabel>
          <NumberInput
            value={minimumOrderValue}
            min={0}
            isDisabled={saving || !canUpdate}
            onChange={(_, v) => setMinimumOrderValue(Number.isNaN(v) ? 0 : v)}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Pedido mínimo p/ resgatar (R$)</FormLabel>
          <NumberInput
            value={minimumRedeemOrderValue}
            min={0}
            isDisabled={saving || !canUpdate}
            onChange={(_, v) =>
              setMinimumRedeemOrderValue(Number.isNaN(v) ? 0 : v)
            }
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      <HStack align="start">
        <FormControl>
          <FormLabel>Tempo de resgate (dias)</FormLabel>
          <NumberInput
            value={pinExpirationDays}
            min={0}
            isDisabled={saving || !canUpdate}
            onChange={(_, v) => setPinExpirationDays(Number.isNaN(v) ? 0 : v)}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl>
          <FormLabel>Fim (opcional)</FormLabel>
          <Input
            type="date"
            value={endAt}
            isDisabled={saving || !canUpdate}
            onChange={(e) => setEndAt(e.target.value)}
          />
        </FormControl>
      </HStack>

      <FormControl isRequired>
        <FormLabel>Canais</FormLabel>
        <CheckboxGroup
          value={channels}
          onChange={(v) => setChannels(v as string[])}
        >
          <HStack>
            <Checkbox value="PDV" isDisabled={saving || !canUpdate}>
              PDV
            </Checkbox>
            <Checkbox value="DELIVERY" isDisabled={saving || !canUpdate}>
              Delivery
            </Checkbox>
          </HStack>
        </CheckboxGroup>
      </FormControl>

      {canUpdate && (
        <HStack justify="flex-end">
          <Button
            colorScheme="yooga"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!canSubmit}
          >
            Salvar campanha
          </Button>
        </HStack>
      )}
    </Stack>
  );
};

const StoreGroupDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const navigate = useNavigate();
  const { e } = usePermissions();
  const { confirm, confirmByText } = useGlobalContext();
  const canRead = e('store-group-read');
  const canUpdate = e('store-group-update');
  const canDelete = e('store-group-delete');
  const bgColor = useColorModeValue('white', 'gray.800');

  const [group, setGroup] = useState<StoreGroup | null>(null);
  const [campaign, setCampaign] = useState<GroupCampaignResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [installationsCount, setInstallationsCount] = useState(0);

  const loadDetail = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await getStoreGroupDetail(groupId);
      setGroup(detail.group);
      setCampaign(detail.campaign);
      setName(detail.group.name);
    } catch {
      setError('Não foi possível carregar o grupo.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (canRead) loadDetail();
  }, [canRead, loadDetail]);

  const handleRename = async () => {
    if (!group || !name.trim() || name.trim() === group.name) return;
    setRenaming(true);
    try {
      await renameStoreGroup(group.id, name.trim());
      toast.success('Grupo renomeado.');
      await loadDetail();
    } catch {
      toast.error('Falha ao renomear o grupo.');
    } finally {
      setRenaming(false);
    }
  };

  const doDelete = async (confirmParam?: boolean) => {
    if (!group) return;
    setDeleting(true);
    try {
      await deleteStoreGroup(group.id, confirmParam);
      toast.success('Grupo excluído.');
      navigate('/store-groups');
    } catch (err) {
      const response = (
        err as {
          response?: {
            status?: number;
            data?: { message?: string; outstanding_cents?: number };
          };
        }
      )?.response;
      if (response?.status === 409) {
        const cents = response.data?.outstanding_cents ?? 0;
        confirmByText(
          'A campanha tem saldo de cashback pendente',
          `Os clientes desse grupo ainda têm R$ ${toReais(cents).toFixed(
            2,
          )} em saldo não resgatado. Excluir o grupo cancela a campanha e esse saldo é perdido. Digite para confirmar mesmo assim.`,
          () => doDelete(true),
        );
      } else {
        toast.error(response?.data?.message ?? 'Falha ao excluir o grupo.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = () => {
    if (!group) return;
    confirm(
      `Excluir o grupo "${group.name}"?`,
      'A campanha de cashback associada a este grupo será cancelada junto. Essa ação não pode ser desfeita.',
      () => doDelete(),
    );
  };

  const handleCreateCampaign = async (payload: CreateGroupCampaignPayload) => {
    if (!group) return;
    setCreatingCampaign(true);
    try {
      await createStoreGroupCampaign(group.id, payload);
      toast.success('Campanha do grupo criada com sucesso.');
      setShowCreateCampaign(false);
      await loadDetail();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Falha ao criar a campanha do grupo.';
      toast.error(message);
    } finally {
      setCreatingCampaign(false);
    }
  };

  if (!canRead) {
    return (
      <Stack
        h="100vh"
        align="center"
        justify="center"
        gap="1.5rem"
        textAlign="center"
        p="2rem"
      >
        <Heading size="lg" color="gray.600">
          Acesso Restrito
        </Heading>
        <Text color="gray.500" maxW="400px">
          Você não possui permissão para acessar os grupos de lojas. Solicite ao
          seu supervisor para liberar o acesso.
        </Text>
        <Box>
          <Button as={RouterLink} to="/" colorScheme="yooga">
            Voltar para a página inicial
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      minH="100vh"
      minW={{ lg: '740px' }}
      w={{ base: '90%', lg: '85%' }}
      m="auto"
      px={{ base: '1rem', lg: '4rem' }}
      pt={{ base: '1rem', lg: '1.5rem' }}
      pb={{ base: '2rem', lg: '4rem' }}
      gap="1.5rem"
    >
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Button
          as={RouterLink}
          to="/store-groups"
          variant="link"
          colorScheme="yooga"
          alignSelf="flex-start"
        >
          ← Voltar para grupos
        </Button>
      </HStack>

      {error && (
        <Stack align="center" py="4rem" gap="1rem">
          <Text color="red.500">{error}</Text>
          <Button colorScheme="yooga" variant="outline" onClick={loadDetail}>
            Tentar novamente
          </Button>
        </Stack>
      )}

      {!error && loading && (
        <HStack justify="center" py="4rem">
          <Spinner size="lg" color="yooga.500" />
          <Text>Carregando…</Text>
        </HStack>
      )}

      {!error && !loading && group && (
        <Tabs colorScheme="yooga">
          <TabList>
            <Tab isDisabled={!campaign}>Relatório</Tab>
            <Tab isDisabled={!campaign}>Clientes</Tab>
            <Tab>Configurações</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px="0">
              {campaign && <GroupReportTab storeGroupId={group.id} />}
            </TabPanel>

            <TabPanel px="0">
              {campaign && <GroupClientsTab storeGroupId={group.id} />}
            </TabPanel>

            <TabPanel px="0">
              <Stack spacing="1.5rem">
                <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
                  <Heading size="md" color="gray.600" mb={4}>
                    Grupo
                  </Heading>
                  <Stack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Nome do grupo</FormLabel>
                      <HStack>
                        <Input
                          value={name}
                          isDisabled={renaming || !canUpdate}
                          onChange={(e) => setName(e.target.value)}
                        />
                        {canUpdate && (
                          <Button
                            colorScheme="yooga"
                            onClick={handleRename}
                            isLoading={renaming}
                            isDisabled={
                              !name.trim() || name.trim() === group.name
                            }
                            flexShrink={0}
                          >
                            Salvar
                          </Button>
                        )}
                      </HStack>
                    </FormControl>

                    {canDelete && (
                      <HStack justify="flex-end">
                        <Button
                          colorScheme="red"
                          variant="outline"
                          onClick={handleDelete}
                          isLoading={deleting}
                        >
                          Excluir grupo
                        </Button>
                      </HStack>
                    )}
                  </Stack>
                </Box>

                <ManageInstallationsSection
                  storeGroupId={group.id}
                  canUpdate={canUpdate}
                  onCountChange={setInstallationsCount}
                />

                <Box bg={bgColor} borderRadius="lg" shadow="sm" p="1.5rem">
                  <Heading size="md" color="gray.600" mb={4}>
                    Campanha de cashback
                  </Heading>
                  {campaign ? (
                    <CampaignEditForm
                      campaign={campaign}
                      canUpdate={canUpdate}
                      onSaved={loadDetail}
                    />
                  ) : (
                    <Stack align="center" py="2rem" gap="1rem">
                      <Text color="gray.500">
                        Este grupo ainda não tem uma campanha de cashback.
                      </Text>
                      {installationsCount < 2 && (
                        <Text color="orange.500" fontSize="sm">
                          Adicione pelo menos 2 lojas participantes antes de
                          criar a campanha.
                        </Text>
                      )}
                      {canUpdate && (
                        <Button
                          colorScheme="yooga"
                          onClick={() => setShowCreateCampaign(true)}
                          isDisabled={installationsCount < 2}
                        >
                          Criar campanha
                        </Button>
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </TabPanel>
          </TabPanels>

          <CreateGroupCampaignModal
            isOpen={showCreateCampaign}
            onClose={() => setShowCreateCampaign(false)}
            onConfirm={handleCreateCampaign}
            submitting={creatingCampaign}
          />
        </Tabs>
      )}
    </Stack>
  );
};

export default StoreGroupDetailPage;
